import {inject, Injectable} from '@angular/core';
import {CycleListType} from '@core/api/data-access';
import {StorageKeys, StorageService} from '@core/storage';
import {ICycleQuery, RouteHistoryService} from '@global-data';

import {CyclesService} from '../../cycles.service';

type SequentialSigningLocalStruct = {
	cycles: string[];
	isFirstSignCycle: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class SequentialSigningService {
	private static readonly SEQUENTIAL_SIGNING_LOCAL_STORAGE_KEY: string = StorageKeys.SignKeys.SEQUENTIAL_SIGNING;
	/* -------------------------------------------------------------------------------------------------------------- */
	private readonly cyclesService: CyclesService = inject(CyclesService);
	private readonly routeHistoryService: RouteHistoryService = inject(RouteHistoryService);
	private readonly storageService: StorageService = inject(StorageService);

	public get isFirstSignCycle(): boolean {
		return this.localStruct?.isFirstSignCycle;
	}

	public set isFirstSignCycle(value: boolean) {
		this.localStruct = {...this.localStruct, isFirstSignCycle: value};
	}

	constructor() {}

	public inSequcentialSigningProcess(): boolean {
		const lastRoute: string[] = this.routeHistoryService.getLatestNthRoutes(1);
		const inSequcentialSigningProcess =
			lastRoute && lastRoute.length > 0 && (lastRoute[0]?.includes('sign') || (this.isFirstSignCycle && lastRoute[0].toLowerCase()?.includes('waiting'))) && this.isInitialized();
		this.isFirstSignCycle = false;
		return inSequcentialSigningProcess;
	}

	public isInitialized(): boolean {
		return this.getCyclesToSign().length > 0;
	}

	public start(cycleIDs?: string[]): void {
		if (!cycleIDs) {
			const queryAllWaitingCycles: ICycleQuery = {
				page: 1,
				size: 0,
				listType: [CycleListType.Waiting],
			};
			this.cyclesService.query(queryAllWaitingCycles).subscribe(response => {
				const cycles = response.cycles?.map(cycle => cycle.id);
				this.setCyclesToSign(cycles);
				this.goToNextCycle();
			});
		} else {
			this.setCyclesToSign(cycleIDs);
			this.goToNextCycle();
		}
	}

	goToNextCycle(activeCycleId?: string): boolean {
		if (activeCycleId) {
			this.removeCycleToSign(activeCycleId);
		}
		const nextCycleId = this.getCyclesToSign()?.at(0);
		if (nextCycleId) {
			window.location.href = `${window.location.origin}/sign/${nextCycleId}`;
			return true;
		}
		return false;
	}

	public reset(): void {
		this.removeAllCyclesToSign();
	}

	/* -------------------------------------------------------------------------------------------------------------- */
	private get localStruct(): SequentialSigningLocalStruct {
		return JSON.parse(sessionStorage.getItem(SequentialSigningService.SEQUENTIAL_SIGNING_LOCAL_STORAGE_KEY));
	}

	private set localStruct(value: SequentialSigningLocalStruct) {
		sessionStorage.setItem(SequentialSigningService.SEQUENTIAL_SIGNING_LOCAL_STORAGE_KEY, JSON.stringify(value));
	}

	private setCyclesToSign(cycles: string[]): void {
		this.localStruct = {...this.localStruct, cycles};
	}

	private getCyclesToSign(): string[] {
		return this.localStruct?.cycles || [];
	}

	private removeCycleToSign(cycleId: string): void {
		const cyclesToSign: string[] = this.getCyclesToSign().filter(cycle => cycle !== cycleId);
		this.setCyclesToSign(cyclesToSign);
	}

	private removeAllCyclesToSign(): void {
		this.setCyclesToSign([]);
	}
}
